import React, { useCallback, useMemo, useState, useRef, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import MessageAttachments from './MessageAttachments';
import { STATUS_TYPES, STATUS_MESSAGES } from '../../../constants/statusTypes';
import MessageMenu from './MessageMenu';
import { getModelConfig } from '../../../constants/models';
import ReactMarkdownMessage from './ReactMarkdownMessage';
import InlineToolsDetails from './InlineToolsDetails';
import AgentLogo from '../../common/logo/AgentLogo';
import TooltipPortal from '../../common/tooltip/TooltipPortal';

import styles from './styles/Message.module.css';

const Message = ({
    message,
    copiedCode,
    onCopyCode,
    setToastMessage,
    handleEditMessage,
    onNavigate,
    siblings = [],
    handleRegenerateMessage,
    parentMessage = null,
    isAgentChat = false,
    agentInfo = null,
    guestChatToken = null,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(message.text);
    const [isReasoningOpen, setIsReasoningOpen] = useState(false);
    const [guestTooltipOpen, setGuestTooltipOpen] = useState(false);
    const editTextareaRef = useRef(null);

    useEffect(() => {
        if (isEditing && editTextareaRef.current) {
            editTextareaRef.current.focus();
            editTextareaRef.current.style.height = 'auto';
            editTextareaRef.current.style.height = editTextareaRef.current.scrollHeight + 'px';
        }
    }, [isEditing]);

    const handleEditChange = useCallback(e => {
        setEditValue(e.target.value);
        // Auto-resize textarea
        e.target.style.height = 'auto';
        e.target.style.height = e.target.scrollHeight + 'px';
    }, []);

    const handleSave = useCallback(() => {
        const trimmedValue = editValue.trim();
        if (trimmedValue) {
            handleEditMessage(message, trimmedValue);
        }
        setIsEditing(false);
    }, [editValue, message, handleEditMessage]);

    const handleCancel = useCallback(() => {
        setEditValue(message.text);
        setIsEditing(false);
    }, [message]);

    const handleIsEditing = useCallback(() => {
        setIsEditing(true);
    }, []);

    const modelId = useMemo(() => message.metadata?.model || null, [message]);
    const modelConfig = useMemo(() => modelId ? (getModelConfig(modelId) || null) : null, [modelId]);

    // Derive image attachments from text links to /api/files/<id>
    const derivedImageAttachments = useMemo(() => {
        const list = [];
        const text = message?.text || '';
        if (typeof text !== 'string' || text.length === 0) return list;

        // Match absolute or relative links to our file proxy endpoint and capture IDs that are
        // either UUID-like (with hyphens) or 32-hex without hyphens
        const urlRegex = /(https?:\/\/[^\s)]+\/api\/files\/([A-Za-z0-9_-]+))|(?:\b\/api\/files\/([A-Za-z0-9_-]+))/g;
        const seen = new Set();
        let match;
        while ((match = urlRegex.exec(text)) !== null) {
            const id = match[2] || match[3];
            if (!id || seen.has(id)) continue;
            seen.add(id);
            const fullUrl = match[1] || match[0];
            list.push({
                id,
                name: 'Image',
                type: 'image/png',
                size: null,
                url: fullUrl,
            });
        }
        return list;
    }, [message.text]);

    const combinedAttachments = useMemo(() => {
        const base = Array.isArray(message.attachments) ? message.attachments : [];
        if (derivedImageAttachments.length === 0) return base;
        const existingIds = new Set(base.map(f => f.id));
        const merged = base.slice();
        derivedImageAttachments.forEach(f => {
            if (!existingIds.has(f.id)) merged.push(f);
        });
        return merged;
    }, [message.attachments, derivedImageAttachments]);

    const authorFirstName = useMemo(() => {
        const msgUser = message.user;
        if (msgUser?.firstName && msgUser.firstName.trim().length > 0) return msgUser.firstName.trim();
        return null;
    }, [message]);

    const renderUserHeader = useCallback(() => {
        if (!isAgentChat || !agentInfo?.isOwner) return null;

        const isGuestUser = !message?.user?.id;
        if (isGuestUser) {
            return (
                <div className={styles.userHeader}>
                    <div
                        onMouseEnter={() => setGuestTooltipOpen(true)}
                        onMouseLeave={() => setGuestTooltipOpen(false)}
                    >
                        <TooltipPortal
                            tooltip={'Message from unauthenticated user'}
                            show={guestTooltipOpen}
                        >
                            <h4 className={styles.userName}>
                                User
                                <span className={styles.badge}>Guest</span>
                            </h4>
                        </TooltipPortal>
                    </div>
                </div>
            );
        }

        // Authenticated user header (no tooltip)
        const displayName = authorFirstName || '-';
        const fallbackInitial = authorFirstName ? authorFirstName.charAt(0).toUpperCase() : '?';
        return (
            <div className={styles.userHeader}>
                <h4 className={styles.userName}>{displayName}</h4>
                {message?.user?.picture ? (
                    <img className={styles.userAvatar} src={message.user.picture} alt={displayName} />
                ) : (
                    <span className={styles.userAvatarFallback}>{fallbackInitial}</span>
                )}
            </div>
        );
    }, [isAgentChat, agentInfo, authorFirstName, message, guestTooltipOpen]);

    /* --- RENDER THOUGHT CONTENT --- */
    const renderThoughtText = useCallback(() => {
        if (message.sender === 'assistant' && message.reasoning_content) {
            return (
                <div className={`${styles.reasoningContent} ${isReasoningOpen ? styles.open : ''}`}>
                    <div className={styles.reasoningText}>
                        <ReactMarkdownMessage
                            copiedCode={copiedCode}
                            onCopyCode={onCopyCode}
                            setToastMessage={setToastMessage}
                        >
                            {message.reasoning_content}
                        </ReactMarkdownMessage>
                    </div>
                </div>
            );
        }
    }, [message, isReasoningOpen, copiedCode, onCopyCode, setToastMessage]);

    const renderThoughtTime = useCallback(() => {
        const reasoningTime = message.metadata?.reasoningTime;
        const reasoningContent = message.reasoning_content;

        if (message.sender === 'assistant' || message.sender === 'agent') {
            if ((reasoningTime || reasoningContent) && (message.status || message.isTyping || modelConfig?.isReasoning)) {
                const reasoningTimeNumber = reasoningTime ? Number(reasoningTime) : 0;
                const roundedReasoningTime = Math.round(reasoningTimeNumber);
                const reasoningTimeText = reasoningTimeNumber === 0 ? 'Thinking...' :
                    reasoningTimeNumber < 1 ?
                        'Thought less than 1 second' :
                        `Thought for ${roundedReasoningTime} seconds`;

                const isThinking = reasoningTimeNumber === 0;

                if (reasoningContent) {
                    return (
                        <div
                            className={styles.reasoningTimeContainer}
                            onClick={() => setIsReasoningOpen(!isReasoningOpen)}
                        >
                            <p className={`${styles.commentText} ${isThinking ? styles.statusMessage : ''}`}>{reasoningTimeText}</p>
                            {isReasoningOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </div>
                    );
                }

                return <p className={`${styles.commentText} ${isThinking ? styles.statusMessage : ''}`}>{reasoningTimeText}</p>;
            } else if (message.status === STATUS_TYPES.THINKING) {
                return (
                    <p className={styles.statusMessage}>{STATUS_MESSAGES[STATUS_TYPES.THINKING]}</p>
                );
            }
        }
    }, [message, modelConfig, isReasoningOpen]);
    /* --- END RENDER THOUGHT CONTENT --- */

    const isShowEditButton = useMemo(() => {
        if (isAgentChat) {
            return false;
        }

        return message.sender === 'user' && 
               message.index > 0 && 
               (!message.attachments || message.attachments.length === 0);
    }, [message, isAgentChat]);

    /* RENDER MESSAGES FOR USER */
    if (message.sender === 'user') {
        return (
            <motion.div
                className={styles.message}
                initial={{ opacity: 0, y: 12, scale: 0.98, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.9 }}
            >
                <MessageAttachments attachments={combinedAttachments || []} />
                {message.text.trim() && (
                    <div className={styles.messageUserContainer}>
                        {isShowEditButton && !isEditing && (
                            <div className={styles.editButtonContainer} />
                        )}
                        {isEditing ? (
                            <div className={styles.editContainer}>
                                <textarea
                                    ref={editTextareaRef}
                                    value={editValue}
                                    onChange={handleEditChange}
                                    className={styles.editTextarea}
                                />
                                <div className={styles.editButtons}>
                                    <button
                                        className={`${styles.btn} ${styles.cancelButton}`}
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`${styles.btn} ${styles.saveButton}`}
                                        onClick={handleSave}
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.messageUserContent}>
                                {renderUserHeader()}
                                <div className={`${styles.messageUser} ${message.attachments?.length > 0 ? styles.hasAttachments : ''}`}>
                                    {message.text}
                                </div>
                                <MessageMenu
                                    message={message}
                                    onNavigate={onNavigate}
                                    siblings={siblings}
                                    handleIsEditing={isShowEditButton ? handleIsEditing : null}
                                />
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        );
    }

    /* RENDER MESSAGES FOR AGENT */
    if (message.sender === 'agent') {
        const renderAgentHeader = () => {
            if (!message.agent) return null;

            return (
                <div className={styles.agentHeader}>
                    <div className={styles.agentInfo}>
                        <AgentLogo
                            logo={message.agent.logo}
                            name={message.agent.name}
                            size="small"
                            className={styles.agentLogo}
                        />
                        <h4 className={styles.agentName}>
                            {message.agent.name || 'Agent'}
                        </h4>
                    </div>
                </div>
            );
        };

        /* RENDER STATUS MESSAGES (FOR AGENT) */
        if (message.status && message.status !== STATUS_TYPES.LOADING && message.status !== STATUS_TYPES.THINKING) {
            return (
                <div className={styles.messageAgent}>
                    <div>
                        {renderAgentHeader()}
                        <div className={`${styles.messageAssistantContentInner} ${styles.statusMessage}`}>
                            <p>{STATUS_MESSAGES[message.status]}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div className={`${styles.messageAgent} ${message.attachments?.length > 0 ? styles.hasAttachments : ''}`}>
                    <div>
                        {renderAgentHeader()}
                        <div className={styles.messageAssistantContentInner}>
                            <InlineToolsDetails
                                tools={message.tools}
                                messageId={message.id}
                                isUsingTool={message.isUsingTool}
                                copiedCode={copiedCode}
                                onCopyCode={onCopyCode}
                                setToastMessage={setToastMessage}
                                isAgentChat={isAgentChat}
                                agentInfo={agentInfo}
                                guestChatToken={guestChatToken}
                            />
                            {renderThoughtTime()}
                            {message.status === STATUS_TYPES.LOADING && <div className={styles.loadingCursor} />}
                            <ReactMarkdownMessage
                                copiedCode={copiedCode}
                                onCopyCode={onCopyCode}
                                setToastMessage={setToastMessage}
                            >
                                {message.text}
                            </ReactMarkdownMessage>
                            {message.isTyping && <div className={styles.loadingCursor} />}
                        </div>
                        <MessageAttachments
                            attachments={combinedAttachments || []}
                            isAssistantMessage={true}
                        />
                        <MessageMenu message={message} />
                    </div>
                </div>
            </div>
        );
    }

    /* RENDER STATUS MESSAGES (FOR ASSISTANT) */
    if (message.status && message.status !== STATUS_TYPES.LOADING && message.status !== STATUS_TYPES.THINKING) {
        return (
            <div className={`${styles.messageAssistant} ${styles.statusMessage}`}>
                <p>{STATUS_MESSAGES[message.status]}</p>
            </div>
        );
    }

    /* RENDER MESSAGES FOR ASSISTANT */
    return (
        <div>
            <div>
                <div className={`${styles.messageAssistant} ${message.attachments?.length > 0 ? styles.hasAttachments : ''}`}>
                    <div>
                        <div className={styles.messageAssistantContentInner}>
                            <InlineToolsDetails
                                tools={message.tools}
                                messageId={message.id}
                                isUsingTool={message.isUsingTool}
                                copiedCode={copiedCode}
                                onCopyCode={onCopyCode}
                                setToastMessage={setToastMessage}
                                toolsRefreshKey={message.toolsRefreshKey || 0}
                            />
                            {renderThoughtTime()}
                            {renderThoughtText()}
                            {message.status === STATUS_TYPES.LOADING && <div className={styles.loadingCursor} />}
                            <ReactMarkdownMessage
                                copiedCode={copiedCode}
                                onCopyCode={onCopyCode}
                                setToastMessage={setToastMessage}
                            >
                                {message.text}
                            </ReactMarkdownMessage>
                            {message.isTyping && <div className={styles.loadingCursor} />}
                        </div>
                        <MessageAttachments
                            attachments={combinedAttachments || []}
                            isAssistantMessage={true}
                        />
                        <MessageMenu
                            message={message}
                            onNavigate={onNavigate}
                            siblings={siblings}
                            handleRegenerateMessage={handleRegenerateMessage}
                            parentMessage={parentMessage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(Message);
